/**
 * Evaluating a SketchUp dynamic component: turning "the module is now 2.10 m
 * wide" into a position and a size for every one of its parts, including the
 * dividers that only exist because a `copies` formula asked for them.
 *
 * The graph is one node per placed instance, carrying the attributes of its
 * definition merged with its own (the instance wins), plus the transform the
 * SKP placed it with. Evaluation walks it top-down: a node's formulas read
 * `parent!` values that are already final, and `copies` expands a node into
 * several instances that each re-evaluate their whole subtree with a different
 * `copy` index — which is exactly how a single divider becomes five.
 */

import {
  evaluateFormula,
  parseFormula,
  toBoolean,
  toNumber,
  type FormulaContext,
  type FormulaNode,
  type FormulaValue,
} from "./formula";
import { fromInches, millimetersToInches, parseLengthUnit, toInches } from "./units";

export interface DynamicAttribute {
  /** The value the SKP stored. Lengths are in inches. */
  value: FormulaValue | null;
  /** Source of the formula that recomputes this attribute, when it has one. */
  formula?: string;
  /** Unit the formula is written in ("CENTIMETERS", "STRING", ...). */
  formulaUnits?: string;
  /** Display unit. Its presence is what marks an attribute as a length. */
  units?: string;
}

export interface DynamicNode {
  /** Stable identity, shared with the imported mesh bindings. */
  path: string;
  /** Name used by `Nome!atributo` references, lowercased. */
  name: string;
  definitionName: string;
  /** Placement in the parent's space: 16 numbers, column-major, inches. */
  transform: number[];
  /** Fallback unit for attributes with no `_formulaunits` of their own. */
  lengthUnits?: string;
  attributes: Record<string, DynamicAttribute>;
  children: DynamicNode[];
}

export interface DynamicComponentGraph {
  schemaVersion: 1;
  /** Size the module had when imported, in millimetres. */
  baseDimensionsMm: { width: number; height: number; depth: number };
  root: DynamicNode;
}

export interface DynamicResizeRequest {
  widthMm?: number;
  heightMm?: number;
  depthMm?: number;
}

export interface EvaluatedPart {
  /** Path of the graph node this part came from — the mesh binding key. */
  path: string;
  /** Unique identity: the path plus the copy index of every replicated ancestor. */
  key: string;
  /** Copy index of each replicated ancestor, outermost first. Empty for an original. */
  copyPath: number[];
  /** True when the part exists only because a `copies` formula asked for it. */
  isCopy: boolean;
  hidden: boolean;
  /** Placement in the module's own space: 16 numbers, column-major, inches. */
  worldMatrix: number[];
  /** The same placement as imported, so a consumer can compute the delta. */
  baseWorldMatrix: number[];
  /** Final size along the component's own axes, in millimetres. */
  sizeMm: { x: number; y: number; z: number };
  /** Formula-resolved size before this resize, in millimetres. */
  anchorSizeMm: { x: number; y: number; z: number };
  /** Absolute length change that produced each local scale, including inheritance. */
  stretchDeltaMm: { x: number; y: number; z: number };
  /** Size as imported, in millimetres. */
  baseSizeMm: { x: number; y: number; z: number };
  /** Offset the formulas placed this part at inside its parent, in inches. */
  localTranslationInches: [number, number, number];
  /** Every attribute that resolved, for the cut list and the properties panel. */
  attributes: Record<string, FormulaValue>;
}

export interface DynamicEvaluation {
  parts: EvaluatedPart[];
  /** Size the module actually took, after its own formulas had their say. */
  dimensionsMm: { width: number; height: number; depth: number };
  /** Formulas that could not be resolved. The part keeps its imported placement. */
  problems: Array<{ path: string; attribute: string; reason: string }>;
}

/**
 * SketchUp's axes against ours: X is width, Y is depth and Z is height. The
 * importer measures the baked GLB, which openskp emits Y-up, so a module
 * 1060 mm wide reads lenx 41.73 in, and its 1008 mm height reads lenz.
 */
const AXIS_BY_DIMENSION = { width: "x", height: "z", depth: "y" } as const;

/** Attributes that place and size a component rather than describe it. */
const POSITION_ATTRIBUTES = ["x", "y", "z"] as const;
const LENGTH_ATTRIBUTES = ["lenx", "leny", "lenz"] as const;

/** Os atributos do próprio SketchUp que nunca são comprimento. */
const RESERVED_NON_LENGTH = new Set([
  "copies", "copy", "hidden", "rotx", "roty", "rotz", "material", "instancename",
  "definitionname", "onclick", "scaletool", "dialogwidth", "dialogheight", "itemcode",
  "description", "summary", "imageurl", "name",
]);

const MAX_COPIES_PER_NODE = 400;
const MAX_PARTS = 20_000;

export function evaluateDynamicComponents(
  graph: DynamicComponentGraph,
  request: DynamicResizeRequest = {},
): DynamicEvaluation {
  // Scale has to be measured against what the formulas produce when nothing is
  // asked for, not against the number the file happens to store. The two differ
  // slightly in real files — a length that reads through several attributes
  // lands a fraction away from what SketchUp last wrote — and anchoring on the
  // stored value turned that fraction into a visible shift the moment a child
  // started inheriting its parent's scale.
  const anchor = hasRequest(request) ? new Evaluator(graph, {}).run() : null;
  return new Evaluator(graph, request, anchor).run();
}

function hasRequest(request: DynamicResizeRequest): boolean {
  return [request.widthMm, request.heightMm, request.depthMm]
    .some((value) => value !== undefined && Number.isFinite(value));
}

/* -------------------------------------------------------------------------- */

interface Instance {
  node: DynamicNode;
  parent: Instance | null;
  /** The `copy` variable: 0 for an original, 1..n for each replica. */
  copyIndex: number;
  /** True when this replica has no geometry of its own and must be cloned. */
  generated: boolean;
  copyPath: number[];
  key: string;
  /** Memoised attribute values, in the units the SKP stores them in. */
  resolved: Map<string, FormulaValue>;
  /** Attributes currently being computed, to catch a formula that loops back. */
  pending: Set<string>;
  /** Attributes whose formula already failed, so each is reported only once. */
  failed: Set<string>;
}

class Evaluator {
  private readonly parts: EvaluatedPart[] = [];
  private readonly problems: DynamicEvaluation["problems"] = [];
  private readonly formulaCache = new Map<string, FormulaNode | Error>();
  private readonly nodesByName = new Map<string, DynamicNode[]>();
  private readonly primaryInstances = new Map<DynamicNode, Instance>();
  private readonly stretchDeltaByInstance = new WeakMap<Instance, { x: number; y: number; z: number }>();

  /** Sizes the formulas settle on with no resize asked for, keyed by part. */
  private readonly anchorSizes: Map<string, EvaluatedPart["sizeMm"]>;
  /** Offsets the formulas settle on with no resize asked for, keyed by part. */
  private readonly anchorOffsets: Map<string, [number, number, number]>;

  constructor(
    private readonly graph: DynamicComponentGraph,
    private readonly request: DynamicResizeRequest,
    anchor: DynamicEvaluation | null = null,
  ) {
    indexByName(graph.root, this.nodesByName);
    this.anchorSizes = new Map((anchor?.parts ?? []).map((part) => [part.key, part.sizeMm]));
    this.anchorOffsets = new Map(
      (anchor?.parts ?? []).map((part) => [part.key, part.localTranslationInches]),
    );
  }

  run(): DynamicEvaluation {
    // Every original instance exists before anything is evaluated. A formula
    // may name a component that sits later in the tree — "Caixa Inferior!alt_i"
    // is read by a sibling declared before it — and creating instances as the
    // walk reaches them would leave those references unresolvable.
    const root = this.buildPrimaryInstances(this.graph.root, null);
    this.emit(root, identityMatrix(), identityMatrix());
    const dimensionsMm = {
      width: this.parts[0]?.sizeMm.x ?? this.graph.baseDimensionsMm.width,
      height: this.parts[0]?.sizeMm.z ?? this.graph.baseDimensionsMm.height,
      depth: this.parts[0]?.sizeMm.y ?? this.graph.baseDimensionsMm.depth,
    };
    return { parts: this.parts, dimensionsMm, problems: this.problems };
  }

  private buildPrimaryInstances(node: DynamicNode, parent: Instance | null): Instance {
    // A replica SketchUp already wrote into the file carries its index in its
    // own name, and that index is the `copy` its formulas read.
    const instance = this.createInstance(
      node,
      parent,
      readMaterialisedCopyIndex(node) ?? 0,
      false,
      parent?.copyPath ?? [],
    );
    for (const child of node.children) this.buildPrimaryInstances(child, instance);
    return instance;
  }

  private createInstance(
    node: DynamicNode,
    parent: Instance | null,
    copyIndex: number,
    generated: boolean,
    parentCopyPath: number[],
  ): Instance {
    // Only a generated replica needs a suffix: one already in the file has a
    // path of its own, and that path is what its baked geometry answers to.
    const copyPath = generated || parentCopyPath.length
      ? [...parentCopyPath, copyIndex]
      : [];
    const instance: Instance = {
      node,
      parent,
      copyIndex,
      generated,
      copyPath,
      key: copyPath.length ? `${node.path}#${copyPath.join(".")}` : node.path,
      resolved: new Map(),
      pending: new Set(),
      failed: new Set(),
    };
    if (!this.primaryInstances.has(node) && !generated) this.primaryInstances.set(node, instance);
    return instance;
  }

  /**
   * Walks one instance, records it as a part and expands its children.
   *
   * The two matrices are the whole point. `frame` is where the component sits;
   * `frame · ratios` is how big it is. Which of the two a child composes off
   * decides whether it follows the parent's stretch — see the note further
   * down, where the children are walked.
   */
  private emit(
    instance: Instance,
    parentFrame: number[],
    parentBaseFrame: number[],
    forceHidden = false,
  ): void {
    if (this.parts.length >= MAX_PARTS) return;
    const node = instance.node;
    const frame = multiplyMatrices(parentFrame, this.frameMatrix(instance));
    const baseFrame = multiplyMatrices(parentBaseFrame, node.transform);
    const sizeMm = this.readSizeMm(instance);
    // The stored LenX/Y/Z can lag behind the formulas in real SketchUp files.
    // Keep the formula-resolved untouched size next to the final one so a
    // renderer can preserve fixed physical margins without reviving that drift.
    const anchorSizeMm = this.anchorSizes.get(instance.key)
      ?? this.anchorSizes.get(instance.node.path)
      ?? sizeMm;

    this.parts.push({
      path: node.path,
      key: instance.key,
      copyPath: instance.copyPath,
      isCopy: instance.generated,
      hidden: forceHidden || this.readHidden(instance),
      worldMatrix: multiplyMatrices(frame, diagonalMatrix(this.ratios(instance))),
      baseWorldMatrix: baseFrame,
      sizeMm,
      anchorSizeMm,
      stretchDeltaMm: this.stretchDeltaMm(instance),
      baseSizeMm: this.baseSizeMm(node),
      localTranslationInches: this.localOffset(instance),
      attributes: Object.fromEntries(instance.resolved),
    });

    for (const family of groupCopyFamilies(node.children)) {
      // Reuse the pre-built instance for an original, so a named reference and
      // the walk share one memoised set of values. A copy of the parent gets
      // fresh children: their `copy` index differs, so their values do too.
      const instanceFor = (child: DynamicNode, index: number): Instance => {
        const primary = this.primaryInstances.get(child);
        if (primary?.parent === instance) return primary;
        return this.createInstance(child, instance, index, false, instance.copyPath);
      };
      const original = instanceFor(family.base, 0);
      // `copies` counts the EXTRA instances to draw. These files also write -1
      // on a part they want gone, but they hide it as well, and the SKP still
      // carries its geometry — so the original always stays and `hidden` is
      // what decides whether it is drawn.
      const copies = Math.max(0, this.readCopies(original));
      this.emit(original, frame, baseFrame);
      for (let index = 1; index <= copies; index += 1) {
        // SketchUp writes the replicas it has already made into the file, with
        // their own geometry. Those are used as they are; only the ones beyond
        // what the file holds have to be cloned.
        const existing = family.copies.get(index);
        const copy = existing
          ? instanceFor(existing, index)
          : this.createInstance(family.base, instance, index, true, instance.copyPath);
        this.emit(copy, frame, baseFrame);
      }
      // A replica the file still carries but the formulas no longer ask for
      // has to be emitted as hidden: its mesh exists and would otherwise stay
      // on screen at its old place.
      for (const [index, child] of family.copies) {
        if (index <= copies) continue;
        this.emit(instanceFor(child, index), frame, baseFrame, true);
      }
    }
  }

  /**
   * The imported placement, displaced by however much the formulas moved this
   * part since the module was left alone.
   *
   * Reading the evaluated x/y/z as an absolute position would mean trusting the
   * formulas to reproduce the file exactly, and they do not: a few offsets in
   * "Gaveteiro Teste" land tens of millimetres from what SketchUp last wrote.
   * Using the difference makes an untouched module identical to its import
   * whatever those formulas say, while still moving every part by the full
   * amount a real resize demands.
   */
  private frameMatrix(instance: Instance): number[] {
    const matrix = [...instance.node.transform];
    // A replica the resize just created has no entry of its own: it did not
    // exist when nothing was asked for. It is a copy of the original, so the
    // original is what its displacement is measured from.
    const anchor = this.anchorOffsets.get(instance.key)
      ?? this.anchorOffsets.get(instance.node.path);
    if (!anchor) return matrix;
    const offset = this.localOffset(instance);
    for (let axis = 0; axis < 3; axis += 1) {
      matrix[12 + axis] = matrix[12 + axis]! + (offset[axis]! - anchor[axis]!);
    }
    return matrix;
  }

  /** Where the formulas put this part inside its parent, in inches. */
  private localOffset(instance: Instance): [number, number, number] {
    const matrix = [...instance.node.transform];
    for (const [axis, attribute] of POSITION_ATTRIBUTES.entries()) {
      const declared = instance.node.attributes[attribute];
      // An offset the component never names is not its own to decide. It rides
      // inside its container and moves with it, per axis: the edge banding of
      // "Gaveteiro Teste" declares its own lengths but no Y, and freezing that
      // Y left the tape at the back edge the panel used to have — 159 mm
      // outside a cabinet that had been made shallower.
      if (declared === undefined) {
        matrix[12 + axis] = this.passiveOffset(instance, axis, matrix[12 + axis]!);
        continue;
      }
      // Declared but with no formula: a fixed placement the author chose.
      if (!declared.formula) continue;
      const value = this.storedNumber(instance, attribute);
      if (!instance.failed.has(attribute) && value !== undefined) {
        matrix[12 + axis] = value;
        continue;
      }
      // The author wrote a formula, so this part was meant to move, and the
      // stored number is only the distance that was right at the imported
      // size. Keeping its share of the parent is far closer than freezing it:
      // in "Movel 1 - Completo" the divider's formula reads `parent!qtd`,
      // which its parent never declares, and holding the distance left it
      // 144 mm away from where the doors meet — holding the proportion puts it
      // back within a millimetre.
      matrix[12 + axis] = matrix[12 + axis]! * this.parentRatio(instance, axis);
    }
    return [matrix[12]!, matrix[13]!, matrix[14]!];
  }

  /**
   * Placement inherited by a child that declares no position of its own.
   *
   * Most passive children belong at the same fraction of their parent, but an
   * edge band belongs to a physical endpoint. Some SKPs bake the band with a
   * fixed setback from that endpoint while their formulas evaluate the panel
   * to a slightly different size. Scaling the baked offset proportionally then
   * scales the setback as well: the 200 mm setback in "Balcão 1 Basc 1 Gav"
   * became 875 mm after increasing its depth. Preserve whichever endpoint was
   * nearest at import so both the current catalogue and future imports keep
   * their real construction margin.
   */
  private passiveOffset(instance: Instance, axis: number, baseOffset: number): number {
    const parent = instance.parent;
    if (!parent || !isEdgeBandContainer(parent.node)) {
      return baseOffset * this.parentRatio(instance, axis);
    }

    const anchor = this.anchorSizes.get(parent.key)
      ?? this.anchorSizes.get(parent.node.path);
    if (!anchor) return baseOffset;

    const axisName = ["x", "y", "z"] as const;
    const anchorLength = anchor[axisName[axis]!] / 25.4;
    if (Math.abs(anchorLength) < 1e-9) return baseOffset;
    const currentLength = anchorLength * this.ratios(parent)[axis]!;
    const endpoints = [
      { distance: Math.abs(baseOffset), offset: baseOffset },
      {
        distance: Math.abs(baseOffset - anchorLength),
        offset: currentLength + (baseOffset - anchorLength),
      },
      {
        distance: Math.abs(baseOffset + anchorLength),
        offset: -currentLength + (baseOffset + anchorLength),
      },
    ];
    endpoints.sort((left, right) => left.distance - right.distance);
    return endpoints[0]!.offset;
  }

  /** How much the containing component grew along one of its own axes. */
  private parentRatio(instance: Instance, axis: number): number {
    if (!instance.parent) return 1;
    return this.ratios(instance.parent)[axis] ?? 1;
  }

  /**
   * How much each axis grew against the untouched module. All ones means
   * unchanged — and with no resize asked for that is exactly what it is, by
   * definition, so nothing can drift merely from being placed.
   */
  private ratios(instance: Instance): [number, number, number] {
    const ratios: [number, number, number] = [1, 1, 1];
    const anchor = this.anchorSizes.get(instance.key)
      ?? this.anchorSizes.get(instance.node.path);
    if (!anchor) return ratios;
    const anchorAxis = [anchor.x, anchor.y, anchor.z];
    // A component that computes none of its lengths is passive: whatever
    // numbers it carries are only what SketchUp last wrote down, not a rule it
    // enforces. The edge banding of "Movel 1 - Completo" is like this — it
    // stores leny = 600 with no formula — and treating that as a decision left
    // a 740 mm tape inside a cabinet cut down to 400.
    const computesLength = LENGTH_ATTRIBUTES.some(
      (name) => instance.node.attributes[name]?.formula,
    );
    for (const [axis, attribute] of LENGTH_ATTRIBUTES.entries()) {
      // An axis the component does not decide is not its own: it is drawn
      // geometry inside a container, and it stretches with it.
      const declared = instance.node.attributes[attribute];
      if (declared === undefined || (!declared.formula && !computesLength)) {
        ratios[axis] = this.parentRatio(instance, axis);
        continue;
      }
      const next = this.lengthInches(instance, attribute);
      const base = anchorAxis[axis]! / 25.4;
      if (next === undefined || Math.abs(base) < 1e-9) continue;
      ratios[axis] = next / base;
    }
    return ratios;
  }

  /**
   * Absolute change behind each ratio. Passive descendants inherit the ratio
   * of their container, but their own stored Len value stays unchanged. The
   * renderer still needs the container's millimetre delta to keep a panel's
   * face and edge banding on the same physical endpoint.
   */
  private stretchDeltaMm(instance: Instance): { x: number; y: number; z: number } {
    const cached = this.stretchDeltaByInstance.get(instance);
    if (cached) return cached;
    const result = { x: 0, y: 0, z: 0 };
    const anchor = this.anchorSizes.get(instance.key)
      ?? this.anchorSizes.get(instance.node.path);
    if (!anchor) {
      this.stretchDeltaByInstance.set(instance, result);
      return result;
    }
    const current = this.readSizeMm(instance);
    const axisName = ["x", "y", "z"] as const;
    const computesLength = LENGTH_ATTRIBUTES.some(
      (name) => instance.node.attributes[name]?.formula,
    );
    for (const [axis, attribute] of LENGTH_ATTRIBUTES.entries()) {
      const name = axisName[axis]!;
      const declared = instance.node.attributes[attribute];
      if (declared === undefined || (!declared.formula && !computesLength)) {
        result[name] = instance.parent ? this.stretchDeltaMm(instance.parent)[name] : 0;
        continue;
      }
      result[name] = current[name] - anchor[name];
    }
    this.stretchDeltaByInstance.set(instance, result);
    return result;
  }

  private readSizeMm(instance: Instance): { x: number; y: number; z: number } {
    const base = this.baseSizeMm(instance.node);
    const read = (attribute: (typeof LENGTH_ATTRIBUTES)[number], fallback: number): number => {
      const value = this.lengthInches(instance, attribute);
      return value === undefined ? fallback : value * 25.4;
    };
    return {
      x: read("lenx", base.x),
      y: read("leny", base.y),
      z: read("lenz", base.z),
    };
  }

  private baseSizeMm(node: DynamicNode): { x: number; y: number; z: number } {
    return {
      x: (this.baseLengthInches(node, "lenx") ?? 0) * 25.4,
      y: (this.baseLengthInches(node, "leny") ?? 0) * 25.4,
      z: (this.baseLengthInches(node, "lenz") ?? 0) * 25.4,
    };
  }

  private baseLengthInches(node: DynamicNode, attribute: string): number | undefined {
    const stored = node.attributes[attribute]?.value;
    if (stored === null || stored === undefined) return undefined;
    const numeric = typeof stored === "number" ? stored : Number(stored);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  private lengthInches(instance: Instance, attribute: string): number | undefined {
    return this.storedNumber(instance, attribute);
  }

  /** An attribute's final value, expressed the way the SKP stores it (inches). */
  private storedNumber(instance: Instance, attribute: string): number | undefined {
    const value = this.demand(instance, attribute);
    if (value === undefined) return undefined;
    const numeric = typeof value === "number" ? value : Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  private readHidden(instance: Instance): boolean {
    if (!instance.node.attributes.hidden) return false;
    const value = this.demand(instance, "hidden");
    return value === undefined ? false : toBoolean(value);
  }

  private readCopies(instance: Instance): number {
    if (!instance.node.attributes.copies) return 0;
    const value = this.demand(instance, "copies");
    if (value === undefined) return 0;
    let copies: number;
    try {
      copies = toNumber(value);
    } catch {
      return 0;
    }
    if (!Number.isFinite(copies)) return 0;
    return Math.min(MAX_COPIES_PER_NODE, Math.round(copies));
  }

  /**
   * The final value of one attribute, computed once per instance. Formulas are
   * evaluated lazily so an attribute that depends on three others pulls them in
   * on demand, in whatever order the author happened to write them.
   */
  private demand(instance: Instance, attribute: string): FormulaValue | undefined {
    const name = attribute.toLocaleLowerCase("en-US");
    if (name === "copy") return instance.copyIndex;
    const cached = instance.resolved.get(name);
    if (cached !== undefined) return cached;

    const declared = instance.node.attributes[name];
    if (!declared) return undefined;

    const storedValue = declared.value === null ? undefined : declared.value;
    if (!declared.formula) {
      // What the user dragged lands on the outermost component's own LenX/Y/Z.
      // Most modules stop there — "Balcão 3 portas" simply stores its width —
      // while one that clamps its size declares a formula, and then the
      // formula wins and reads the request through `parent!lenx` instead.
      const requested = instance.parent === null ? this.requestedInches(name) : undefined;
      const value = requested ?? storedValue;
      if (value !== undefined) instance.resolved.set(name, value);
      return value;
    }
    // A formula that already failed keeps the imported value and stays quiet:
    // the same attribute is read several times per part and one broken formula
    // should not fill the report with copies of itself.
    if (instance.failed.has(name)) return storedValue;
    if (instance.pending.has(name)) {
      instance.failed.add(name);
      this.report(instance, name, "a fórmula depende de si mesma");
      return storedValue;
    }

    const compiled = this.compile(declared.formula);
    if (compiled instanceof Error) {
      instance.failed.add(name);
      this.report(instance, name, compiled.message);
      return storedValue;
    }

    instance.pending.add(name);
    try {
      const raw = evaluateFormula(compiled, this.contextFor(instance));
      const value = this.storeAs(instance.node, name, declared, raw);
      instance.resolved.set(name, value);
      return value;
    } catch (error) {
      instance.failed.add(name);
      this.report(instance, name, error instanceof Error ? error.message : String(error));
      return storedValue;
    } finally {
      instance.pending.delete(name);
    }
  }

  private contextFor(instance: Instance): FormulaContext {
    const readFrom = (target: Instance | null, name: string): FormulaValue | undefined => {
      if (!target) return undefined;
      const value = this.demand(target, name);
      if (value === undefined) return undefined;
      return this.readAs(target.node, name, value);
    };
    return {
      self: (name) => (name === "copy" ? instance.copyIndex : readFrom(instance, name)),
      parent: (name) => (instance.parent
        ? readFrom(instance.parent, name)
        : this.requestedLength(name)),
      named: (name, attribute) => {
        const node = this.resolveNamed(instance.node, name);
        if (!node) return undefined;
        const target = this.primaryInstances.get(node);
        return target ? readFrom(target, attribute) : undefined;
      },
    };
  }

  /**
   * A name can repeat across a module — "Frente" appears four times in "Balcão
   * 3 portas" — so the nearest one wins: the search climbs the ancestors and
   * takes the first candidate living inside each, which is what an author
   * writing `Caixa Inferior!alt_i` means.
   */
  private resolveNamed(from: DynamicNode, name: string): DynamicNode | undefined {
    const candidates = this.nodesByName.get(name);
    if (!candidates?.length) return undefined;
    if (candidates.length === 1) return candidates[0];
    const segments = from.path.split(" / ");
    for (let depth = segments.length; depth > 0; depth -= 1) {
      const scope = segments.slice(0, depth).join(" / ");
      const inside = candidates.find(
        (candidate) => candidate.path === scope || candidate.path.startsWith(`${scope} / `),
      );
      if (inside) return inside;
    }
    return candidates[0];
  }

  /** The size the user asked for, in inches, or the imported size when untouched. */
  private requestedInches(name: string): number | undefined {
    const millimetres = name === "lenx" ? this.request.widthMm
      : name === "leny" ? this.request.depthMm
      : name === "lenz" ? this.request.heightMm
      : undefined;
    if (millimetres === undefined || !Number.isFinite(millimetres)) return undefined;
    return millimetersToInches(millimetres);
  }

  /**
   * What the outermost component's container offers: the size the user asked
   * for. An axis left out of the request falls back to the size the module was
   * imported at, so evaluating with no request reproduces the file untouched.
   */
  private requestedLength(name: string): FormulaValue | undefined {
    const root = this.graph.root;
    const inches = this.requestedInches(name) ?? this.baseLengthInches(root, name);
    if (inches === undefined) return undefined;
    return fromInches(inches, this.unitOf(root, name));
  }

  /** Converts a stored value into the units the reading formula expects. */
  private readAs(node: DynamicNode, name: string, value: FormulaValue): FormulaValue {
    const unit = this.unitOf(node, name);
    if (!unit || typeof value === "boolean") return value;
    const numeric = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numeric)) return value;
    return fromInches(numeric, unit);
  }

  /** Converts a formula's result back into the inches the SKP stores. */
  private storeAs(
    node: DynamicNode,
    name: string,
    declared: DynamicAttribute,
    value: FormulaValue,
  ): FormulaValue {
    /*
     * Alguns arquivos deixam o último ramo de uma tabela como texto vazio,
     * embora o SKP tenha gravado no atributo o último número válido. Isso
     * acontece na profundidade das gavetas dos lixeiros MDF: uma dependência
     * externa ausente faz `PC` cair em "", que virava zero e anulava o curso
     * de abertura inteiro. Para atributo numericamente armazenado, vazio não é
     * uma nova medida; é ausência de resultado, portanto mantém-se o valor que
     * o arquivo importado comprovadamente usou. Zero explícito continua zero.
     */
    if (typeof value === "string" && value.trim() === "") {
      const stored = declared.value;
      const numeric = typeof stored === "number" ? stored
        : typeof stored === "string" && stored.trim() ? Number(stored) : Number.NaN;
      if (Number.isFinite(numeric)) return stored as FormulaValue;
    }
    const unit = this.unitOf(node, name, declared);
    if (!unit || typeof value === "boolean") return value;
    const numeric = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numeric)) return value;
    return toInches(numeric, unit);
  }

  /**
   * The unit a formula is written in. Positions and lengths always carry one:
   * when the attribute declares no `_formulaunits`, the component's
   * `_lengthunits` applies — which is how `_z_formula` produces centimetres
   * without saying so.
   */
  private unitOf(node: DynamicNode, name: string, declared?: DynamicAttribute) {
    const attribute = declared ?? node.attributes[name];
    const explicit = parseLengthUnit(attribute?.formulaUnits);
    if (explicit) return explicit;
    if (attribute?.formulaUnits !== undefined && attribute.formulaUnits !== null) {
      // A declared non-length unit ("STRING", "FLOAT") means no conversion.
      const declaredUnit = String(attribute.formulaUnits).trim();
      if (declaredUnit) return null;
    }
    const positional = (POSITION_ATTRIBUTES as readonly string[]).includes(name)
      || (LENGTH_ATTRIBUTES as readonly string[]).includes(name);
    if (positional) return parseLengthUnit(node.lengthUnits) ?? parseLengthUnit(attribute?.units);
    const declaredUnits = parseLengthUnit(attribute?.units);
    if (declaredUnits) return declaredUnits;
    // "FLOAT" e "STRING" são a marca de que não é comprimento: uma contagem
    // como AQG=4 não pode virar 4 polegadas.
    if (attribute?.units !== undefined && attribute.units !== null
      && String(attribute.units).trim()) return null;
    /*
     * Sem marca nenhuma, vale a unidade de exibição do componente.
     *
     * O SKP guarda todo comprimento em polegada e diz em `_lengthunits` como o
     * arquivo o mostra. Um atributo de marcenaria que não declara unidade — o
     * `ZAF` do gaveteiro guarda 0,47244, que são 12 mm — saía cru daqui e a
     * fórmula que o lia em centímetro entendia 0,47 cm. O fundo da gaveta
     * nascia 11 mm estreito e as contrafrentes 8 mm curtas.
     *
     * Vale só para atributo que a marcenaria criou. Os do próprio SketchUp que
     * não são medida — `Copies` conta réplicas, `Hidden` liga e desliga, os
     * `Rot*` são graus — continuam crus por mais que estejam sem marca.
     */
    if (RESERVED_NON_LENGTH.has(name)) return null;
    return typeof attribute?.value === "number" ? parseLengthUnit(node.lengthUnits) : null;
  }

  private compile(source: string): FormulaNode | Error {
    const cached = this.formulaCache.get(source);
    if (cached) return cached;
    let result: FormulaNode | Error;
    try {
      result = parseFormula(source);
    } catch (error) {
      result = error instanceof Error ? error : new Error(String(error));
    }
    this.formulaCache.set(source, result);
    return result;
  }

  private report(instance: Instance, attribute: string, reason: string): void {
    if (this.problems.length >= 200) return;
    this.problems.push({ path: instance.node.path, attribute, reason });
  }
}

/* -------------------------------------------------------------------------- */

/**
 * SketchUp does not keep a replica as a formula: once a `copies` attribute has
 * produced them, it writes them into the file as real instances named
 * "<algo> copy 001", each with its own baked geometry and a mirrored copy of
 * the `copies` attribute.
 *
 * Reading that name back matters twice over. The index is the `copy` variable
 * the replica's own formulas depend on — "Gaveteiro Teste" places its drawers
 * with `copy*parent!lenz/parent!qg + LenZ`, so every drawer that came in as
 * copy 0 landed on the bottom one. And knowing a replica already exists stops
 * it being created a second time: four drawers each asking for three more made
 * sixteen, stacked.
 */
const MATERIALISED_COPY = /^(.*?)[\s_]+copy[\s_]+(\d+)$/i;

function readMaterialisedCopyIndex(node: DynamicNode): number | null {
  const segment = node.path.split(" / ").at(-1) ?? "";
  const match = MATERIALISED_COPY.exec(segment.trim());
  if (!match) return null;
  const index = Number(match[2]);
  return Number.isSafeInteger(index) && index > 0 ? index : null;
}

interface CopyFamily {
  base: DynamicNode;
  /** Replicas already in the file, by their index. */
  copies: Map<number, DynamicNode>;
}

/**
 * Pairs each replica with the instance it was made from. They are matched by
 * the dynamic-component name, because the original is usually unnamed — the
 * drawers read "Gaveta copy 001" while their original is just "Component_13549"
 * — and both carry the same `_name`.
 *
 * A family is only formed when a replica is actually present and exactly one
 * sibling could be its original, so two independent components that happen to
 * share a name are left alone.
 */
function groupCopyFamilies(children: readonly DynamicNode[]): CopyFamily[] {
  const replicas = children.filter((child) => readMaterialisedCopyIndex(child) !== null);
  if (!replicas.length) return children.map((base) => ({ base, copies: new Map() }));

  const claimed = new Set<DynamicNode>();
  const families: CopyFamily[] = [];
  for (const replica of replicas) {
    if (claimed.has(replica)) continue;
    const key = familyKey(replica);
    const candidates = children.filter(
      (child) => readMaterialisedCopyIndex(child) === null && familyKey(child) === key,
    );
    if (candidates.length !== 1) continue;
    const base = candidates[0]!;
    if (claimed.has(base)) continue;
    const copies = new Map<number, DynamicNode>();
    for (const sibling of children) {
      const index = readMaterialisedCopyIndex(sibling);
      if (index === null || familyKey(sibling) !== key) continue;
      copies.set(index, sibling);
      claimed.add(sibling);
    }
    claimed.add(base);
    families.push({ base, copies });
  }
  for (const child of children) {
    if (!claimed.has(child)) families.push({ base: child, copies: new Map() });
  }
  return families;
}

function familyKey(node: DynamicNode): string {
  if (node.name) return `name:${node.name}`;
  const segment = node.path.split(" / ").at(-1) ?? "";
  const match = MATERIALISED_COPY.exec(segment.trim());
  return `def:${node.definitionName}:${match ? match[1]!.trim() : segment}`;
}

function indexByName(node: DynamicNode, index: Map<string, DynamicNode[]>): void {
  const seen = new Set<string>();
  for (const key of [node.name, node.definitionName]) {
    const normalized = key?.trim().toLocaleLowerCase("en-US");
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    const bucket = index.get(normalized);
    if (bucket) bucket.push(node);
    else index.set(normalized, [node]);
  }
  for (const child of node.children) indexByName(child, index);
}

function isEdgeBandContainer(node: DynamicNode): boolean {
  const label = `${node.name} ${node.definitionName}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("en-US")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  return /(^| )(bordas?|edges?|edge bands?)( |$)/.test(label);
}

export function identityMatrix(): number[] {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
}

function diagonalMatrix([x, y, z]: [number, number, number]): number[] {
  return [x, 0, 0, 0, 0, y, 0, 0, 0, 0, z, 0, 0, 0, 0, 1];
}

/** Column-major 4x4 product, matching the layout the SketchUp SDK hands over. */
export function multiplyMatrices(left: number[], right: number[]): number[] {
  const result = new Array<number>(16).fill(0);
  for (let column = 0; column < 4; column += 1) {
    for (let row = 0; row < 4; row += 1) {
      let total = 0;
      for (let k = 0; k < 4; k += 1) {
        total += left[k * 4 + row]! * right[column * 4 + k]!;
      }
      result[column * 4 + row] = total;
    }
  }
  return result;
}

export { AXIS_BY_DIMENSION };
